<template>
  <div class="login-container">
    <div class="login-language">
      <LanguageSwitcher />
    </div>
    <div class="login-card">
      <!-- Left section: decorative panel with logo -->
      <div class="login-left">
        <div class="login-left-bg">
          <div class="login-waves" aria-hidden="true">
            <svg viewBox="0 0 200 100" preserveAspectRatio="none">
              <path d="M0 50 Q50 20 100 50 T200 50 V100 H0 Z" fill="rgba(255,255,255,0.08)" />
              <path d="M0 60 Q50 30 100 60 T200 60 V100 H0 Z" fill="rgba(255,255,255,0.06)" />
              <path d="M0 70 Q50 40 100 70 T200 70 V100 H0 Z" fill="rgba(255,255,255,0.04)" />
            </svg>
          </div>
        </div>
        <div class="login-logo-wrapper">
          <img :src="logoUrl" :alt="t('app.title')" class="login-logo" />
        </div>
      </div>

      <!-- Right section: form -->
      <div class="login-right">
        <h1 class="login-welcome">{{ t('auth.welcomeTitle') }}</h1>
        <p class="login-subtitle">{{ t('auth.welcomeSubtitle') }}</p>

        <v-form ref="form" class="login-form" @submit.prevent="handleSubmit">
          <v-text-field
            v-model="username"
            :label="t('auth.username')"
            name="username"
            type="text"
            :error-messages="usernameError"
            variant="outlined"
            density="comfortable"
            bg-color="grey-lighten-4"
            class="login-field"
            required
            @blur="validateUsername"
          />

          <v-text-field
            v-model="password"
            :label="t('auth.password')"
            name="password"
            :type="showPassword ? 'text' : 'password'"
            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            :error-messages="passwordError"
            variant="outlined"
            density="comfortable"
            bg-color="grey-lighten-4"
            class="login-field"
            required
            @click:append-inner="showPassword = !showPassword"
            @blur="validatePassword"
          />

          <div class="login-forgot-wrap">
            <v-btn
              variant="text"
              size="small"
              color="grey-darken-1"
              class="text-none px-0"
              @click="forgotPassword"
            >
              {{ t('auth.forgotPassword') }}
            </v-btn>
          </div>

          <v-btn
            type="submit"
            :loading="authStore.loading"
            color="primary"
            size="large"
            block
            class="login-submit text-none"
            :disabled="!username || !password"
          >
            {{ t('auth.login').toUpperCase() }}
          </v-btn>

          <v-checkbox
            v-model="rememberMe"
            :label="t('auth.rememberMe')"
            color="primary"
            density="compact"
            hide-details
            class="login-remember"
          />
        </v-form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useProjectConfigStore } from '@/stores/projectConfig';
import { useNotificationStore } from '@/stores/notification';
import { useI18n } from 'vue-i18n';
import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue';

const router = useRouter();
const authStore = useAuthStore();
const notificationStore = useNotificationStore();
const projectConfigStore = useProjectConfigStore();
const { t } = useI18n();
const logoUrl = computed(() => projectConfigStore.logoUrl ?? '/logo.png');

const form = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);
const username = ref('');
const password = ref('');
const rememberMe = ref(false);
const showPassword = ref(false);
const usernameError = ref('');
const passwordError = ref('');

const validateUsername = () => {
  usernameError.value = !username.value ? t('auth.usernameRequired') : '';
};

const validatePassword = () => {
  passwordError.value = !password.value ? t('auth.passwordRequired') : '';
};

const handleSubmit = async () => {
  usernameError.value = '';
  passwordError.value = '';
  validateUsername();
  validatePassword();
  if (usernameError.value || passwordError.value) {
    return;
  }
  if (form.value) {
    const { valid } = await form.value.validate();
    if (valid) {
      try {
        await authStore.login({
          username: username.value,
          password: password.value,
        });
        await new Promise((resolve) => setTimeout(resolve, 100));
        if (!authStore.hasUiAccess) {
          authStore.logout();
          notificationStore.showError(t('auth.loginDeniedByRole'));
          return;
        }
        router.replace('/dashboard');
      } catch (error) {
        console.error('Login failed:', error);
      }
    }
  }
};

const forgotPassword = () => {
  alert(t('auth.forgotPasswordMessage'));
};
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  min-height: 100dvh;
  background: linear-gradient(170deg, #f0f4fa 0%, #f8f9fb 40%, #ffffff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px) + 60px);
  position: relative;
  box-sizing: border-box;
  overflow-y: auto;
}

.login-language {
  position: absolute;
  top: 24px;
  right: 24px;
}

.login-card {
  display: flex;
  width: 100%;
  max-width: 920px;
  min-height: 520px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}

/* Left section: ~42% width, blue gradient, big logo */
.login-left {
  flex: 0 0 42%;
  position: relative;
  background: linear-gradient(180deg, #5b8def 0%, #3d6bc5 50%, #2c4d96 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 32px;
}

.login-left-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.login-waves {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
}

.login-waves svg {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.login-logo-wrapper {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 280px;
}

.login-logo {
  max-width: 100%;
  width: auto;
  height: auto;
  max-height: 200px;
  object-fit: contain;
  display: block;
}

.login-app-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #fff;
  text-align: center;
  letter-spacing: 0.02em;
}

/* Right section: form */
.login-right {
  flex: 1;
  padding: 48px 56px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.login-welcome {
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: rgb(var(--v-theme-primary));
  text-align: left;
  margin: 0 0 4px 0;
  line-height: 1.2;
}

.login-subtitle {
  font-size: 0.9rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
  margin: 0 0 32px 0;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.login-field :deep(.v-field) {
  border-radius: 12px;
}

.login-forgot-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: -8px;
}

.login-submit {
  margin-top: 8px;
  letter-spacing: 0.04em;
}

.login-remember {
  margin-top: 4px;
}

/* Responsive: stack on small screens */
@media (max-width: 768px) {
  .login-card {
    flex-direction: column;
    max-width: 440px;
  }

  .login-left {
    flex: 0 0 auto;
    min-height: 200px;
    padding: 32px 24px;
  }

  .login-logo {
    max-height: 120px;
  }

  .login-right {
    padding: 32px 24px;
  }
}

/* Center the loading spinner in the button */
:deep(.v-btn .v-progress-circular) {
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
}

:deep(.v-btn--loading .v-btn__content) {
  opacity: 0 !important;
}

:deep(.v-btn.v-btn--loading .v-progress-circular) {
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
}
</style>
